import { AccountSourceCache } from '../../../lib/cache/AccountNameCache';
import { SettingsCache } from '~/lib/consts/settings-cache';
import ItemIsNotReady from '../../../lib/helpers/ItemIsNotReady';
import ItemIsReady from '../../../lib/helpers/ItemIsReady';
import { SmartSendFilterContext, SmartSendItemFilterApplier } from '../../../lib/helpers/SmartSendFilterApplier';
import { SelectionOption } from './SelectionOption';
import { SelectionOptionGroup } from './SelectionOptionGroup';
import {
  filterMasterItems,
  SmartSendHasSource,
  IsDomestic,
  IsInternational,
  IsSelected,
  IsSavedForLater
} from '../../../lib/helpers/SmartSendSummaryGenerator';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { onlyUniqueNonBlank } from '../../../lib/helpers/ArrayHelper';

const GenerateQuickSelectOptions = (
  itemsCache: SmartSendItemApiModel[],
  restrictToSold: boolean,
  filter: SmartSendFilterContext,
  providerName: (provider: string) => string
) => {
  const output: SelectionOptionGroup[] = [];

  const items = SmartSendItemFilterApplier(itemsCache, filter).filter(filterMasterItems);

  // Quick Select Toggles
  if (SettingsCache.settings.Features.includes('MarketplaceQuickSelect')) {
    const marketplaceOptions: SelectionOption[] = [];

    const sources = { ...AccountSourceCache };

    // Add the sources based on the items
    items.forEach((i) => {
      sources[i.Source.Id] ||= i.Source.Name ?? i.Source.Id;
      i.MarketplaceOrderLines?.forEach((m) => {
        sources[m.Source.Id] ||= m.Source.Name ?? m.Source.Id;
      });
    });

    for (const s in sources) {
      if (s == 'grouped') {
        continue;
      }
      const count = items.filter((i) => SmartSendHasSource(i, s)).length;
      if (count > 0) {
        marketplaceOptions.push({
          selected: items.filter((i) => SmartSendHasSource(i, s) && i.State?.selected).length,
          total: count,
          name: `${sources[s]}`,
          action: 'TOGGLE_SOURCE',
          source: s
        });
      }
    }

    // Only Add if we have more than 1
    if (marketplaceOptions.length > 1) {
      output.push({ name: 'Marketplace', items: marketplaceOptions });
    }
  }

  const domCount = items.filter((i) => IsDomestic(i)).length;
  const intCount = items.filter((i) => IsInternational(i)).length;

  if (domCount > 0 && intCount > 0) {
    output.push({
      name: 'Lane',
      items: [
        {
          selected: domCount == 0 ? 0 : items.filter((i) => IsSelected(i) && IsDomestic(i)).length,
          total: domCount,
          name: 'Domestic Orders',
          action: 'TOGGLE_DOMESTIC'
        },
        {
          selected: intCount == 0 ? 0 : items.filter((i) => IsSelected(i) && IsInternational(i)).length,
          total: intCount,
          name: 'International Orders',
          action: 'TOGGLE_INTERNATIONAL'
        }
      ]
    });
  }

  if (restrictToSold) {
    const readyCount = items.filter((i) => ItemIsReady(i)).length;
    const notReadyCount = items.filter((i) => ItemIsNotReady(i)).length;
    const savedForLater = items.filter((i) => IsSavedForLater(i)).length;

    output.push({
      name: 'Status',
      items: [
        {
          selected: readyCount == 0 ? 0 : items.filter((i) => IsSelected(i) && ItemIsReady(i)).length,
          total: readyCount,
          name: 'Ready',
          action: 'TOGGLE_READY'
        },
        {
          selected: notReadyCount == 0 ? 0 : items.filter((i) => IsSelected(i) && ItemIsNotReady(i)).length,
          total: notReadyCount,
          name: 'Not Ready',
          action: 'TOGGLE_NOTREADY'
        },
        {
          selected: savedForLater == 0 ? 0 : items.filter((i) => IsSelected(i) && IsSavedForLater(i)).length,
          total: savedForLater,
          name: 'Saved For Later',
          action: 'TOGGLE_SAVEDFORLATER'
        }
      ]
    });

    const providers: SelectionOptionGroup = { name: 'Provider', items: [] };
    items
      .map((x) => x.Service?.Provider)
      .filter(onlyUniqueNonBlank)
      .forEach((s) => {
        const selectedServices = items.filter((i) => i.Service?.Provider === s);
        providers.items.push({
          selected: selectedServices.filter(IsSelected).length,
          total: selectedServices.length,
          name: `${providerName(s)} Services`,
          action: 'TOGGLE_PROVIDER',
          provider: s
        });
      });
    if (providers.items.length > 1) {
      output.push(providers);
    }
  }

  return output
    .map((x) => ({ ...x, items: x.items.filter((i) => i.total > 0) }))
    .filter((x) => x.items.filter((f) => f.total > 0).length > 1);
};
export default GenerateQuickSelectOptions;
